import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import * as axios from 'axios';

import { ToastContainer, toast } from 'react-toastify';
import Flatpickr from "react-flatpickr";
import Popup from "reactjs-popup";
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import Tippy, { tippy } from '@tippyjs/react';

import PageSectionMenu from './PageSectionMenu';
import { SectionTitle } from './ClassicSectionCard';
import {
  offsetDateForBrowser
} from '../../../shared/timeZoneLogic';
import {
  INVERTED_WINDOW_WARNING, TIMED_ENTRY_UNUSABLE_WINDOW_WARNING,
  UNSET_TIMED_ENTRY_WARNING, DOORS_AFTER_START_WARNING, doorsAfterStart,
  isInternalActivity, publishNotYetSettable, PUBLISH_PENDING_TOOLTIP,
} from '../../shared/utils/showDetailWarnings';

const onDayCreate = (dObj, dStr, fp, dayElem) => {
  let dateStr = dayElem.getAttribute('aria-label');
  let date = new Date(dateStr);
  let tooltipContent = "";
  let showTooltip = false;

  let normalizedEventStart = new Date(fp.eventStart.getFullYear(), fp.eventStart.getMonth(), fp.eventStart.getDate());
  let normalizedEventEnd = new Date(fp.eventEnd.getFullYear(), fp.eventEnd.getMonth(), fp.eventEnd.getDate());

  if(date && (fp.isRequired || fp.isEventEnd)){
    if(fp.isRequired && !fp.isEventEnd && date > normalizedEventEnd) {
      tooltipContent = `${fp.showActivity.name} must be on or before Event End`;
      showTooltip = true;
    }

    if(fp.isEventEnd && date < normalizedEventStart) {
      tooltipContent = "Event End must be on or after Event Start";
      showTooltip = true;
    }
  }

  if(showTooltip) {
    tippy(dayElem, {
      content: tooltipContent,
      zIndex: 999999
    });
  }
};

const advanceSentPopupContentStyle = {
  "background": "var(--sl-bg-primary)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "210px",
  "maxHeight": "300px",
  "overflowY": "auto",
  "padding": "12px 20px"
}

const modalContentStyle = {
  "background": "var(--sl-bg-primary)",
  "border": "none",
  "borderRadius": "8px",
  "boxShadow": "0 0 1px 1px rgba(0,0,0,0.02), 0 4px 6px 1px rgba(0,0,0,0.06)",
  "width": "800px",
  "padding": "20px"
}

const hiddenActivityStyle = {
  textDecoration: "line-through",
  color: "var(--od-c-6c757d, #6c757d)",
  opacity: 0.5
}

const visibilityToggleContainerStyle = {
  backgroundColor: "var(--od-c-f3f4f6, #f4f5f7)",
  borderRadius: "8px",
  padding: "4px",
  border: "1px solid var(--od-c-e5e7eb, #e4e6eb)"
}

const visibilityToggleCountStyle = {
  backgroundColor: "var(--od-c-e5e7eb, #e4e6eb)",
  color: "var(--od-c-535862, #52525b)",
  borderRadius: "10px",
  padding: "0 8px",
  marginLeft: "6px",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "18px"
}

const visibilityToggleDotStyle = {
  display: "inline-block",
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: "#FF595E",
  marginLeft: "6px"
}

const canSubmitForm = (showActivity) => {
  return (
    showActivity
      && showActivity.name
      && showActivity.name.length > 0
      && showActivity.start_date_midday
      && showActivity.start_time_with_wrong_date
  );
}

const canAddNewShowActivity = (showActivities) => {
  return !showActivities.some((showActivity) => !showActivity.id);
}

const parseDateTime = (dateTime) => {
  if (dateTime){
    if (typeof(dateTime) === "string") {
      var dateObj = new Date(dateTime);
      return dateObj;
    } else {
      return dateTime;
    }
  }
}

const getShowActivity = (showActivities, name) => {
  if (showActivities.length > 0) {
    return showActivities.find((showActivity) => showActivity.name === name);
  }
}

const flatPickrFormat = "n/j/y at h:iK"

const ManageShowActivities = ({
  csrfToken,
  team,
  confirm,
  currentUser,
  fetchConfirmShowActivities,
  showActivities,
  deleteShowActivity,
  editShowActivity,
  cancelEditShowActivity,
  addNewShowActivity,
  showActivityNameChanged,
  showActivityStartDateChanged,
  showActivityStartTimeChanged,
  showActivityCopy,
  createShowActivity,
  updateShowActivity,
  showAdvanceSends,
  venuePreSalePromoCode,
  confirmPageSection,
  fetchPermissionSet,
  updateConfirmPageSection,
}) => {
  const [searchedShowActivities, setSearchedShowActivities] = useState([]);
  const [selectedShowActivity, setSelectedShowActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [controller, setController] = useState(null);
  const [isAddShowActivityOpen, setIsAddShowActivityOpen] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState('visible');

  const { visibleCount, hiddenCount, totalCount, filteredShowActivities } = useMemo(() => {
    // Internal rows (Publish) sit outside the visible/hidden split: they are
    // never on the advance, so counting them as "Hidden" would misreport what the
    // fan sees, and filtering them normally would hide them under the default
    // "Visible" view — which is the one list meant to show every date and time.
    const external = showActivities.filter((a) => !isInternalActivity(a));
    const total = external.length;
    const visible = external.filter((a) => a.show_in_advance).length;
    const filtered = showActivities.filter((a) => {
      if (a._editing) return true;
      if (isInternalActivity(a)) return true;
      if (visibilityFilter === 'hidden') return !a.show_in_advance;
      if (visibilityFilter === 'all') return true;
      return a.show_in_advance;
    });
    return {
      visibleCount: visible,
      hiddenCount: total - visible,
      totalCount: total,
      filteredShowActivities: filtered
    };
  }, [showActivities, visibilityFilter]);

  var detailsLabel = team.mode === "ATTRACTIONS_MANAGER" ? "Event Details" : "Show Details";

  useEffect(() => {
    fetchConfirmShowActivities(csrfToken, team, confirm);
  }, [])

  const handleOnSearchShowActivities = async (query) => {
    setIsLoading(true);

    controller && controller.abort()
    const newController = new AbortController();
    setController(newController)

    const searchedShowActivities = await axios.get(`/teams/${team.id}/show_activity_settings/autocomplete?name=${query}`,{
        signal: newController.signal
     });

    setIsLoading(false);
    setSearchedShowActivities(searchedShowActivities.data.concat({ name: `Add "${query}"` }));
  };

  useEffect(() => {
    if (selectedShowActivity.length > 0) {
      handleOnSearchShowActivities(selectedShowActivity[0].name);
    }
  }, [selectedShowActivity])

  const addNewShowActivityDetail = async () => {
    axios.post(
      `/teams/${team.id}/show_activity_settings`,
      {show_activity_setting: selectedShowActivity[0]}
    )
    .then(function (response) {
      setIsAddShowActivityOpen(false);

      toast.success("Saved a new show activity detail", {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true
      });
    })
    .catch(function (error) {
      let message;
      try {
        message = error.response.data[0];
      }
      catch (err) {
        message = "Something went wrong while saving a new show activity detail";
      }

      toast.error(message, {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: true,
        autoClose: 5000,
        hideProgressBar: true
      });
    });
  }

  const canManageActivities = currentUser.can_manage_show_activities
    && confirm.calendar_classification !== "past";

  const showActivityAlertWarning = (showActivity) => {
    if (showActivity.sales_window_inverted) {
      return INVERTED_WINDOW_WARNING;
    } else if (doorsAfterStart(showActivities, showActivity)) {
      return DOORS_AFTER_START_WARNING;
    } else if (showActivity.has_tickets && !showActivity.set) {
      return "This date is being used to specify the availability of one or more ticket types. Please input a date and time to ensure tickets go on sale as intended.";
    } else if (showActivity.has_timed_entry_tickets && !showActivity.set) {
      return UNSET_TIMED_ENTRY_WARNING;
    } else if (showActivity.timed_entry_window_unusable) {
      // Below the sales window rather than beside it: this chain renders one
      // string, and a row carrying both problems takes the same remedy either
      // way — move one of the two dates.
      return TIMED_ENTRY_UNUSABLE_WINDOW_WARNING;
    } else if (showActivity.name === "Public On-Sale" && showActivity.set && !showActivity.has_on_sale_tickets) {
      return "No tickets match this on-sale date.";
    } else if (showActivity.name === "Venue Pre-Sale" && showActivity.set && !showActivity.has_tickets && !venuePreSalePromoCode) {
      return "The Pre-Sale campaign will not fire until a ticket & promo code is associated with this show detail."
    } else {
      return null;
    }
  };

  const maxDateBasedOnShowActivity = (showActivity) => {
    if(showActivity.required && showActivity.name !== "Event End"){
      return (offsetDateForBrowser(getShowActivity(showActivities, "Event End")?.start_time || null));
    } else {
      return null;
    }
  };

  const minDateBasedOnShowActivity = (showActivity) => {
    if(showActivity.name === "Event End"){
      return (offsetDateForBrowser(getShowActivity(showActivities, "Event Start")?.start_time || new Date()));
    } else {
      return null;
    }
  };

  return (
    <div>
      <ToastContainer icon={false} />
      <div className="card-body pb-0">
        <div className="row pb-3">
          <div className="col">
            <SectionTitle title={detailsLabel}
                          locked={Boolean(confirmPageSection.permission_set_id)} />
          </div>
          <div className="col-xs-auto"
               style={{"paddingRight": "15px"}}>
            {showAdvanceSends.length > 0 ? (
              <Popup arrow={false}
                    offsetY={5}
                    position="bottom right"
                    contentStyle={advanceSentPopupContentStyle}
                    on={"hover"}
                    trigger={open => (
                      <a href="#"
                         onClick={
                           (e) => {
                             e.preventDefault();
                           }
                         }
                         className="text-muted">
                        <i className="fal fa-paper-plane mr-2"></i>
                        <u>Advance Sent</u>
                      </a>
                    )} >
               {close => (
                 <div className="row">
                   <div className="col-12">
                     {showAdvanceSends.map((showAdvanceSend, index) =>
                       <p key={index}
                          className={"small text-muted " + (index + 1 === showAdvanceSends.length ? "mb-0" : "")}>
                        Advance sent on {showAdvanceSend.human_created_at} to {showAdvanceSend.to_as_sentence}
                       </p>
                     )}
                   </div>
                 </div>
               )}
              </Popup>
            ) : null}
            <PageSectionMenu csrfToken={csrfToken}
                             team={team}
                             confirm={confirm}
                             currentUser={currentUser}
                             confirmPageSection={confirmPageSection}
                             fetchPermissionSet={fetchPermissionSet}
                             updateConfirmPageSection={updateConfirmPageSection} />
          </div>
        </div>
        {showActivities.length > 0 ? (
          <div className="pb-3">
            <div className="d-inline-flex align-items-center" style={visibilityToggleContainerStyle}>
              {[
                { key: 'visible', label: 'Visible', count: visibleCount },
                { key: 'hidden', label: 'Hidden', count: hiddenCount, dot: hiddenCount > 0 },
                { key: 'all', label: 'All', count: totalCount }
              ].map((option) => {
                const selected = visibilityFilter === option.key;
                return (
                  <button key={option.key}
                          type="button"
                          onClick={() => setVisibilityFilter(option.key)}
                          className="btn btn-sm d-inline-flex align-items-center"
                          style={{
                            backgroundColor: selected ? "var(--od-c-ffffff, #fff)" : "transparent",
                            boxShadow: selected ? "0 1px 2px rgba(0,0,0,0.1)" : "none",
                            fontWeight: selected ? 600 : 400,
                            color: selected ? "var(--od-c-1a1a1a, #1a1a1a)" : "var(--od-c-535862, #52525b)",
                            border: "none"
                          }}>
                    {option.label}
                    <span style={visibilityToggleCountStyle}>
                      {option.count}
                    </span>
                    {option.dot ? (
                      <span style={visibilityToggleDotStyle} />
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
      {showActivities.length === 0 ? (
        <div className="card-body pt-0">
          <div className="card no-border mb-0" style={{'background': 'var(--od-c-f3f9fc, #f4f9fc)'}}>
            <div className="card-body">
              <div className="row">
                <div className="col-12 col-md-6 col-lg-4 order-md-2 text-left text-md-center">
                  <i className="fal fa-clipboard-list"
                     style={{"fontSize": "120px", "color": "rgba(25, 130, 196, .5)"}}></i>
                </div>
                <div className="col-12 col-md-6 col-lg-8 order-md-1">
                  <h5>Add {detailsLabel}</h5>
                  <p className="text-muted">
                    Build your show schedule and share with the team to ensure the event runs smoothly.
                  </p>
                  {canManageActivities ? (
                    <a href="#"
                       onClick={
                         (e) => {
                           e.preventDefault();

                           if(canAddNewShowActivity(showActivities)){
                             addNewShowActivity(showActivityCopy, confirm);
                           }
                         }
                       }
                       className="btn btn-primary">
                      <strong>Add Activity</strong>
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <form className="form-inline"
                  onSubmit={
                    (e) => {
                      e.preventDefault();

                      var showActivity = showActivities
                        .find((showActivity) => showActivity._editing);

                      if(!showActivity){
                        return false;
                      }

                      if(!canSubmitForm(showActivity)){
                        return false;
                      }

                      if(showActivity.id){
                        updateShowActivity(csrfToken, team, showActivity);
                      } else {
                        createShowActivity(csrfToken, team, showActivity, confirm);
                      }

                      setSelectedShowActivity([]);
                    }
                  }>
              <div className="table-responsive responsive-card-table-wrapper">
              <table className="table table-sm table-borderless vertical-middle responsive-card-table show-activities-table">
                <colgroup>
                  <col span={1} style={{"width": "auto", "minWidth": "200px"}} />
                  <col span={1} style={{"width": "auto", "minWidth": "250px"}} />
                  <col span={1} style={{"width": "150px", "minWidth": "100px"}} />
                </colgroup>
                <thead>
                  <tr className="text-muted">
                    <th style={{"paddingLeft": "25px"}}>Activity</th>
                    <th>Date and Time</th>
                    <th style={{"paddingRight": "25px"}}>&nbsp;</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShowActivities.sort((a, b) => {return new Date(a.start_time) - new Date(b.start_time)}).map((showActivity, index) => (
                    showActivity._editing ? (
                      <tr key={index} style={{"background": "var(--od-c-f3f9fc, #f4f9fc)"}}>
                        {showActivity.required ? (
                          <td className="overflow-ellipsis" style={{"paddingLeft": "25px"}} >
                            <span title={showActivity.name}>
                              {showActivity.name}
                            </span>
                          </td>
                        ) : (
                          <td style={{"paddingLeft": "25px"}} >
                            <div className="form-group">
                              <AsyncTypeahead
                                id="show-activity-search"
                                className="w-100"
                                positionFixed
                                labelKey="name"
                                filterBy={() => true}
                                isLoading={false}
                                options={searchedShowActivities}
                                placeholder=""
                                useCache={false}
                                selected={selectedShowActivity}
                                onSearch={handleOnSearchShowActivities}
                                onChange={
                                  (value) => {
                                    if (value.length === 0) {
                                      return;
                                    }
                                    if (value[0].name.includes('Add "')) {
                                      setIsAddShowActivityOpen(true);
                                      setSelectedShowActivity(selectedShowActivity);
                                      return;
                                    }

                                    showActivityNameChanged(value[0].name);
                                    setSelectedShowActivity(value);
                                  }
                                }
                                onInputChange={
                                  (value) => {
                                    setSelectedShowActivity([{...showActivity, name: value}]);
                                    showActivityNameChanged(value);
                                  }
                                }
                              />
                              { selectedShowActivity.length > 0 && (
                                <Popup
                                  open={isAddShowActivityOpen}
                                  contentStyle={modalContentStyle}
                                  closeOnDocumentClick={false}
                                  className={"offer-modal"}
                                  onClose={() => {
                                  }}
                                >
                                  <div>
                                    <div className="row">
                                      <div className="col">
                                        <div className="row">
                                          <div className="col-sm-12">
                                            <h5 style={{"marginBottom": "16px"}}>
                                              <strong>Add a new field</strong>
                                            </h5>
                                          </div>
                                          <div className="col-sm-12">
                                            <p>
                                              Add this new field to show details.
                                            </p>
                                          </div>
                                          <div className="col-sm-12 d-flex align-items-center" style={{"gap": "30px"}}>
                                            <input
                                              type="text"
                                              className="form-control"
                                              value={selectedShowActivity[0].name}
                                              onChange={(e) => {
                                                setSelectedShowActivity([{...selectedShowActivity[0], name: e.target.value}])
                                                showActivityNameChanged(e.target.value);
                                              }}
                                            />
                                            <div className="custom-control custom-checkbox custom-checkbox-table d-flex">
                                              <input
                                                type="checkbox"
                                                className="custom-control-input"
                                                checked={selectedShowActivity[0].default}
                                                onChange={(e) => {
                                                  setSelectedShowActivity([{...selectedShowActivity[0], default: e.target.checked}])
                                                }}
                                                id={`new-show-activity-${index}`}
                                              />
                                              <label
                                                className="custom-control-label"
                                                htmlFor={`new-show-activity-${index}`}
                                              >
                                                Default
                                              </label>
                                              <Tippy
                                                content={
                                                  <div style={{
                                                        "fontSize": "12px"
                                                      }}>
                                                    Default fields will automatically be added to {detailsLabel} when a new confirm is created.
                                                  </div>
                                                }
                                                placement={'top'}
                                                maxWidth={200}
                                                animation={'shift-away'}
                                                theme='material'
                                              >
                                                <i className="fas fa-info-circle text-muted ml-2"></i>
                                              </Tippy>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="text-right">
                                          <a href="#"
                                            onClick={
                                              (e) => {
                                                e.preventDefault();
                                                setIsAddShowActivityOpen(false);
                                              }
                                            }>
                                            Cancel
                                          </a>
                                          <a href="#"
                                            className="btn btn-primary ml-3"
                                            onClick={
                                              (e) => {
                                                e.preventDefault();
                                                addNewShowActivityDetail();
                                              }
                                            }>
                                            Add
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </Popup>
                              )}
                            </div>
                          </td>
                        )}
                        <td >
                          <div className="form-group">
                            <div style={{"backgroundColor": "var(--od-c-ffffff, #fff)"}} className="flatpickr-date-input px-2 d-flex align-items-center">
                              <div className="pr-2" style={{"fontSize": "18px"}}>
                                <i className="far fa-calendar"></i>
                              </div>
                              <Flatpickr
                                options={{
                                  onDayCreate: (dObj, dStr, fp, dayElem) => {
                                    fp = {
                                      ...fp,
                                      showActivity: showActivity,
                                      isRequired: showActivity.required,
                                      isEventEnd: showActivity.name === "Event End",
                                      eventStart: offsetDateForBrowser(getShowActivity(showActivities, "Event Start").start_time),
                                      eventEnd: offsetDateForBrowser(getShowActivity(showActivities, "Event End").start_time)
                                    }
                                    onDayCreate(dObj, dStr, fp, dayElem);
                                  },
                                  enableTime: true,
                                  dateFormat: flatPickrFormat,
                                  maxDate:  maxDateBasedOnShowActivity(showActivity),
                                  minDate: minDateBasedOnShowActivity(showActivity)
                                }}
                                placeholder={"Select date / time"}
                                value={showActivity.start_time_with_wrong_date}
                                onClose={dates => {
                                  showActivityStartTimeChanged(dates[0]);
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="text-right" style={{"paddingRight": "25px"}} >
                          <a href="#"
                             onClick={
                               (e) => {
                                 e.preventDefault();
                                 cancelEditShowActivity(showActivityCopy);
                                 setSelectedShowActivity([]);
                               }
                             }>
                            Cancel
                          </a>
                          <button type="submit"
                                  style={{"borderRadius": "8px"}}
                                  disabled={!canSubmitForm(showActivity)}
                                  className="btn btn-primary btn-sm ml-2">
                            Save
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={index} style={{
                        backgroundColor: showActivityAlertWarning(showActivity) ? "var(--sl-bg-error-primary)" : "transparent"
                      }}>
                        {/* Internal rows are permanently off the advance, so the
                            struck-through "hidden" treatment would be on forever and
                            would read as something being wrong. */}
                        <td className="overflow-ellipsis" data-label="Activity" style={{paddingLeft: "25px", ...(showActivity.show_in_advance || isInternalActivity(showActivity) ? null : hiddenActivityStyle)}} >
                          <span title={showActivity.name}>
                            {showActivity.name}
                              {!!showActivityAlertWarning(showActivity) ? (
                                <span style={{paddingLeft: 5}}>
                                  <Tippy
                                    content={
                                      <div className="text-center">
                                        {showActivityAlertWarning(showActivity)}
                                      </div>
                                    }
                                    placement="top"
                                    animation="shift-away"
                                    theme="material"
                                  >
                                    <i className="fa-solid fa-triangle-exclamation"
                                       role="img"
                                       aria-label={showActivityAlertWarning(showActivity)}
                                       style={{color: "#FF5555" }}/>
                                  </Tippy>
                                </span>
                              ) : null}
                              {showActivity.name === "Venue Pre-Sale" && !!venuePreSalePromoCode ? (
                                <span style={{paddingLeft: 5}}>
                                  <Tippy
                                    content={
                                      <div className="text-center">
                                        Code: {venuePreSalePromoCode.name}
                                      </div>
                                    }
                                    placement="top"
                                    animation="shift-away"
                                    theme="material"
                                  >
                                    <i className="fa-regular fa-badge-percent" style={{color: "var(--od-c-888888, #888888)" }}/>
                                  </Tippy>
                                </span>
                              ) : null}
                          </span>
                        </td>
                        <td data-label="Date & Time" style={showActivity.show_in_advance || isInternalActivity(showActivity) ? undefined : hiddenActivityStyle}>
                          <span>
                            {showActivity.set ? (
                              <>
                                {showActivity.human_start_date}
                                {showActivity.human_start_time ? ` | ${showActivity.human_start_time}` : null}
                              </>
                            ) : null}
                          </span>
                        </td>
                        <td className="text-right" data-label="Actions" style={{"paddingRight": "25px"}} >
                          {canManageActivities ? (
                            <div>
                              {/* No advance toggle on internal rows: they are never on the
                                  advance, and the toggle posts set: true, which the server
                                  refuses for an unpublished Publish row. */}
                              {!isInternalActivity(showActivity) && (showActivity.show_in_advance ? (
                                <Tippy
                                  content={
                                    <div className='text-center' style={{"fontSize": "10px"}}>
                                      Visible on advance.<br />
                                      Click to hide.
                                    </div>
                                  }
                                  placement={'top'}
                                  animation={'shift-away'}
                                  theme='material'
                                >
                                  <a href="#"
                                      className="text-muted"
                                      onClick={
                                        (e) => {
                                          e.preventDefault();

                                          var updated = Object.assign(showActivity, {}, {
                                            show_in_advance: false
                                          });

                                          updateShowActivity(csrfToken, team, updated);
                                        }
                                      }>
                                    <i className="fas fa-eye mr-2"></i>
                                  </a>
                                </Tippy>
                              ) : (
                                <Tippy
                                  content={
                                    <div className='text-center' style={{"fontSize": "10px"}}>
                                      Hidden from advance.<br />
                                      Click to show.
                                    </div>
                                  }
                                  placement={'top'}
                                  animation={'shift-away'}
                                  theme='material'
                                >
                                  <a href="#"
                                      className="text-muted"
                                      onClick={
                                        (e) => {
                                          e.preventDefault();

                                          var updated = Object.assign(showActivity, {}, {
                                            show_in_advance: true
                                          });

                                          updateShowActivity(csrfToken, team, updated);
                                        }
                                      }>
                                    <i className="fas fa-eye-slash mr-2 text-muted" style={{"opacity": "50%"}}></i>
                                  </a>
                                </Tippy>
                              ))}
                              {publishNotYetSettable(showActivity) ? (
                                <Tippy
                                  content={
                                    <div className='text-center' style={{"fontSize": "12px"}}>
                                      {PUBLISH_PENDING_TOOLTIP}
                                    </div>
                                  }
                                  placement={'top'}
                                  animation={'shift-away'}
                                  theme='material'
                                >
                                  <i className="fas fa-pencil text-muted"
                                     aria-disabled="true"
                                     style={{"opacity": "40%", "cursor": "not-allowed"}}></i>
                                </Tippy>
                              ) : (
                                <a href="#"
                                   className="text-muted"
                                   onClick={
                                     (e) => {
                                       e.preventDefault();
                                       editShowActivity(showActivity);
                                       setSelectedShowActivity([showActivity])
                                     }
                                   }>
                                  <i className="fas fa-pencil"></i>
                                </a>
                              )}
                              {showActivity.required ? (
                                <Tippy
                                  content={
                                    <div style={{"fontSize": "12px"}}>
                                      Required
                                    </div>
                                  }
                                  placement={'top'}
                                  animation={'shift-away'}
                                  theme='material'
                                >
                                  <i className="text-optional ml-2 fas fa-lock"></i>
                                </Tippy>
                              ) : (
                                <a href="#"
                                   className="text-muted ml-2"
                                   onClick={
                                     (e) => {
                                       e.preventDefault();
                                       deleteShowActivity(csrfToken, team, showActivity);
                                     }
                                   }>
                                  <i className="fas fa-trash"></i>
                                </a>
                              )}
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
              </div>
            </form>
            {canManageActivities ? (
              <div className="card-body pt-3">
                <a href="#"
                   className={(canAddNewShowActivity(showActivities) ? "" : "disabled")}
                   onClick={
                     (e) => {
                       e.preventDefault();

                       if(canAddNewShowActivity(showActivities)){
                         addNewShowActivity(showActivityCopy, confirm);
                       }
                     }
                   }>
                  <i className="fas fa-plus-circle"></i>
                  &nbsp;
                  <strong>Add Activity</strong>
                </a>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  )
};

ManageShowActivities.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  fetchConfirmShowActivities: PropTypes.func.isRequired,
  showActivities: PropTypes.array,
  deleteShowActivity: PropTypes.func.isRequired,
  editShowActivity: PropTypes.func.isRequired,
  cancelEditShowActivity: PropTypes.func.isRequired,
  addNewShowActivity: PropTypes.func.isRequired,
  showActivityNameChanged: PropTypes.func.isRequired,
  showActivityStartDateChanged: PropTypes.func.isRequired,
  showActivityStartTimeChanged: PropTypes.func.isRequired,
  showActivityCopy: PropTypes.object,
  createShowActivity: PropTypes.func.isRequired,
  updateShowActivity: PropTypes.func.isRequired,
  showAdvanceSends: PropTypes.array,
  venuePreSalePromoCode: PropTypes.object,
  confirmPageSection: PropTypes.object,
  fetchPermissionSet: PropTypes.func.isRequired,
  updateConfirmPageSection: PropTypes.func.isRequired,
};

export default ManageShowActivities;
